// src/components/TranslationProjects.js

import React, { useState, useEffect, useMemo } from 'react';
import {
    Container, Typography, Box, Button, Grid, Card, CardContent,
    LinearProgress, IconButton, Menu, MenuItem, Chip, Breadcrumbs,
    Link, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, ToggleButtonGroup, ToggleButton,
    Avatar, AvatarGroup, Tooltip, Autocomplete, Tabs, Tab, Drawer,
    Checkbox, FormControl, Select, InputLabel, Divider
} from '@mui/material';
import Flag from 'react-world-flags';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import TranslateIcon from '@mui/icons-material/Translate';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CircularProgress } from '@mui/material';

// Icons for word/segment counts
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import GrainIcon from '@mui/icons-material/Grain';

import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../slices/authSlice';
import isoLanguages from './isoLanguages';
import { getProjectStatus, statusColor, getProjectProgress } from '../utils/projectStatus';
import { stringToPastelColor, stringToDarkColor } from '../utils/colorUtils';

const INITIAL_VISIBLE_COUNT = 9;
const LOAD_MORE_STEP = 9;

const getLanguageDetails = (code) =>
    isoLanguages.find((lang) => lang.code.toLowerCase() === code?.toLowerCase()) || {
        name: code,
        countryCode: 'UN',
    };

const getProjectLangCodes = (project) =>
    Array.isArray(project.targetLanguages)
        ? project.targetLanguages.map((l) => (typeof l === 'string' ? l : l.code))
        : [project.targetLanguage];

const getProjectLangNames = (project) =>
    getProjectLangCodes(project).map((c) => getLanguageDetails(c).name);

const getFirstProjectId = (project) =>
    Array.isArray(project.targetLanguages) && project.targetLanguages.length > 0
        ? project.targetLanguages[0].projectId || project.targetLanguages[0]
        : project._id;

const getInitial = (str) =>
    typeof str === 'string' && str.length > 0 ? str[0].toUpperCase() : '';

export default function TranslationProjects() {
    const [createdProjects, setCreatedProjects] = useState([]);
    const [sharedProjects, setSharedProjects] = useState([]);
    
    // New tab state
    const [tabValue, setTabValue] = useState(0); // 0 = My Projects, 1 = Shared With Me
    
    // UI state
    const [viewMode, setViewMode] = useState('card');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState([]);
    const [tagFilter, setTagFilter] = useState([]);
    const [allUniqueTags, setAllUniqueTags] = useState([]);
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
    
    // Sort & Pin state
    const [sortBy, setSortBy] = useState('createdAt'); // 'createdAt', 'deadline', 'progress'
    const [pinnedProjects, setPinnedProjects] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('pinnedProjects')) || [];
        } catch {
            return [];
        }
    });

    // Bulk selections
    const [selectedProjects, setSelectedProjects] = useState([]);

    // Quick peek drawer
    const [drawerProject, setDrawerProject] = useState(null);

    const [downloadingProject, setDownloadingProject] = useState(null);
    const [downloadingType, setDownloadingType] = useState(null);

    const token = useSelector((state) => state.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.get('/api/projects?summary=true', {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const created = (response.data.createdProjects || []);
                const shared = (response.data.sharedProjects || []);

                setCreatedProjects(created);
                setSharedProjects(shared);

                const tags = new Set();
                created.forEach(p => (p.tags || []).forEach(t => tags.add(t)));
                shared.forEach(p => (p.tags || []).forEach(t => tags.add(t)));
                setAllUniqueTags(Array.from(tags).sort());
            } catch (err) {
                if (err.response?.status === 401) {
                    dispatch(logout());
                    navigate('/login', { replace: true });
                } else {
                    console.error('Error fetching projects:', err);
                }
            }
        };

        if (token) {
            fetchProjects();
        }
    }, [token, navigate]);

    useEffect(() => {
        // Reset visible count when filters or tab changes
        setVisibleCount(INITIAL_VISIBLE_COUNT);
        setSelectedProjects([]); // Reset selection on tab change
    }, [search, statusFilter, tagFilter, tabValue, sortBy]);

    useEffect(() => {
        localStorage.setItem('pinnedProjects', JSON.stringify(pinnedProjects));
    }, [pinnedProjects]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const togglePin = (e, projectId) => {
        e.stopPropagation();
        setPinnedProjects(prev => {
            if (prev.includes(projectId)) return prev.filter(id => id !== projectId);
            return [...prev, projectId];
        });
    };

    const toggleSelection = (e, projectId) => {
        e.stopPropagation();
        setSelectedProjects(prev => {
            if (prev.includes(projectId)) return prev.filter(id => id !== projectId);
            return [...prev, projectId];
        });
    };

    const handleSelectAll = (e, projectsInView) => {
        e.stopPropagation();
        if (selectedProjects.length === projectsInView.length) {
            setSelectedProjects([]);
        } else {
            setSelectedProjects(projectsInView.map(p => p._id));
        }
    };

    const handleDownload = async (projectId, fileType = 'translated') => {
        setDownloadingProject(projectId);
        setDownloadingType(fileType);
        try {
            const response = await axios.get(
                `/api/export/${projectId}/download?type=${fileType}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                }
            );
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${projectId}-${fileType}`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) fileName = match[1];
            }
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
        } finally {
            setDownloadingProject(null);
            setDownloadingType(null);
        }
    };

    const handleBulkDownload = async (type) => {
        // In a real scenario, you'd hit a bulk zip endpoint.
        // For now, downloading them sequentially.
        for (const projId of selectedProjects) {
            await handleDownload(projId, type);
        }
        setSelectedProjects([]);
    };

    const handleExportAsXliff = async (projectId) => {
        try {
            const response = await axios.get(`/api/projects/${projectId}/xliff`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/xml' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${projectId}.xliff`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting XLIFF:', err);
        }
    };

    const handleDeleteProject = async (projectId) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;
        try {
            await axios.delete(`/api/projects/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCreatedProjects((prev) => prev.filter((p) => p._id !== projectId));
            setSharedProjects((prev) => prev.filter((p) => p._id !== projectId));
            setSelectedProjects(prev => prev.filter(id => id !== projectId));
        } catch (err) {
            console.error('Error deleting project:', err);
        }
    };

    // Unified filtering logic
    const currentProjects = tabValue === 0 ? createdProjects : sharedProjects;
    
    const filteredAndSortedProjects = useMemo(() => {
        let filtered = currentProjects.filter((project) => {
            const projectName = (project.projectName || '').toLowerCase();
            const status = getProjectStatus(project);
            const sourceLang = getLanguageDetails(project.sourceLanguage).name.toLowerCase();
            const targetNames = getProjectLangNames(project).join(' ').toLowerCase();

            const matchesSearch =
                projectName.includes(search.toLowerCase()) ||
                status.includes(search.toLowerCase()) ||
                sourceLang.includes(search.toLowerCase()) ||
                targetNames.includes(search.toLowerCase());

            const matchesStatus = statusFilter.length === 0 || statusFilter.includes(status);
            const matchesTags = tagFilter.length === 0 || (project.tags && tagFilter.every(tag => project.tags.includes(tag)));

            return matchesSearch && matchesStatus && matchesTags;
        });

        // Sorting
        filtered.sort((a, b) => {
            // Pinned always on top
            const aPinned = pinnedProjects.includes(a._id);
            const bPinned = pinnedProjects.includes(b._id);
            if (aPinned && !bPinned) return -1;
            if (!aPinned && bPinned) return 1;

            if (sortBy === 'deadline') {
                const dateA = a.deadline ? new Date(a.deadline) : new Date(8640000000000000);
                const dateB = b.deadline ? new Date(b.deadline) : new Date(8640000000000000);
                return dateA - dateB;
            } else if (sortBy === 'progress') {
                return getProjectProgress(b) - getProjectProgress(a);
            } else {
                // Default createdAt descends
                return new Date(b.createdAt) - new Date(a.createdAt);
            }
        });

        return filtered;
    }, [currentProjects, search, statusFilter, tagFilter, sortBy, pinnedProjects]);

    const displayedProjects = filteredAndSortedProjects.slice(0, visibleCount);

    const toggleStatus = (statusKey) => {
        setStatusFilter((prev) =>
            prev.includes(statusKey) ? prev.filter((s) => s !== statusKey) : [...prev, statusKey]
        );
    };

    const handleViewToggle = (_, nextView) => {
        if (nextView) setViewMode(nextView);
    };

    const breadcrumbs = location.pathname
        .split('/')
        .filter((x) => x)
        .map((path, idx, arr) => (
            <Link
                key={path}
                color={idx === arr.length - 1 ? 'text.primary' : 'inherit'}
                onClick={() => navigate(`/${arr.slice(0, idx + 1).join('/')}`)}
                sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
            >
                {path}
            </Link>
        ));


    // Computations helper
    const getCounts = (project) => {
        const sections = Array.isArray(project.sections) ? project.sections : [];
        const segmentCount = typeof project.segmentCount === 'number' ? project.segmentCount : sections.length;
        const computedWordCount = typeof project.wordCount === 'number' ? project.wordCount : sections.reduce((sum, sec) => {
            const words = sec.split(/\s+/).filter(Boolean).length;
            return sum + words;
        }, 0);
        return { segmentCount, computedWordCount };
    };

    const ProjectCard = ({ project, isSharedView }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleOpen = (e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
        };
        const handleClose = () => {
            setAnchorEl(null);
        };

        const progress = getProjectProgress(project);
        const isFullyTranslated = progress >= 100;
        const isPinned = pinnedProjects.includes(project._id);
        const isSelected = selectedProjects.includes(project._id);

        const { segmentCount, computedWordCount } = getCounts(project);

        return (
            <Card
                sx={{
                    borderRadius: 3,
                    boxShadow: isSelected ? 8 : 3,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: '0.2s',
                    border: isSelected ? '2px solid' : '2px solid transparent',
                    borderColor: isSelected ? 'primary.main' : 'transparent',
                    '&:hover': { boxShadow: 8, transform: 'translateY(-2px)' },
                    mb: 3,
                }}
                onClick={() => navigate(`/dashboard/projects/${project._id}`)}
            >
                <CardContent sx={{ position: 'relative' }}>
                    {/* Top actions corner */}
                    <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 3 }}>
                        <Checkbox 
                            checked={isSelected}
                            onClick={(e) => toggleSelection(e, project._id)}
                        />
                    </Box>

                    <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 12, right: 48, zIndex: 2 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setDrawerProject(project);
                        }}
                    >
                        <InfoOutlinedIcon />
                    </IconButton>

                    <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
                        onClick={handleOpen}
                    >
                        <MoreVertIcon />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!isSharedView && (
                            <MenuItem onClick={() => { navigate(`/dashboard/projects/${project._id}/edit`); handleClose(); }}>
                                <i className="mdi mdi-pencil me-1"></i>Edit
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => { navigate(`/dashboard/projects/${getFirstProjectId(project)}/translate`); handleClose(); }}>
                            <TranslateIcon fontSize="small" sx={{ mr: 1 }} />Translate
                        </MenuItem>
                        <MenuItem onClick={() => { handleDownload(getFirstProjectId(project), 'original'); handleClose(); }}>
                            <i className="mdi mdi-download me-1"></i>Download Original
                        </MenuItem>
                        <MenuItem 
                            onClick={() => { handleDownload(getFirstProjectId(project), 'translated'); handleClose(); }} 
                            disabled={!isFullyTranslated}
                        >
                            <i className="mdi mdi-download me-1"></i>Download Translated
                        </MenuItem>
                        <MenuItem onClick={() => { handleExportAsXliff(getFirstProjectId(project)); handleClose(); }}>
                            <i className="mdi mdi-file-export me-1"></i>Export as XLIFF
                        </MenuItem>
                        {!isSharedView && (
                            <MenuItem onClick={() => { handleDeleteProject(project._id); handleClose(); }}>
                                <i className="mdi mdi-delete me-1"></i>Delete
                            </MenuItem>
                        )}
                    </Menu>

                    {/* Content */}
                    <Box display="flex" alignItems="center" gap={1} mb={1} mt={4}>
                        <IconButton size="small" onClick={(e) => togglePin(e, project._id)}>
                            {isPinned ? <PushPinIcon color="primary" fontSize="small" sx={{ transform: 'rotate(45deg)' }}/> : <PushPinOutlinedIcon fontSize="small" color="disabled"/>}
                        </IconButton>
                        <Typography variant="h6" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.projectName}
                        </Typography>
                    </Box>

                    {isSharedView && (
                        <Box mb={1}>
                            <Chip size="small" label="Assigned" color="secondary" variant="outlined" />
                        </Box>
                    )}

                    <Box display="flex" gap={2} mt={1} mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Flag code={getLanguageDetails(project.sourceLanguage).countryCode} style={{ width: 22 }} />
                            <Typography variant="body2" color="text.secondary">
                                {getLanguageDetails(project.sourceLanguage).name}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Flag code={getLanguageDetails(getProjectLangCodes(project)[0]).countryCode} style={{ width: 22 }} />
                            <Typography variant="body2" color="text.secondary">
                                {getProjectLangNames(project).join(', ')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box mb={2} display="flex" alignItems="center" gap={3}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <LibraryBooksIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">{computedWordCount}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <GrainIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">{segmentCount}</Typography>
                        </Box>
                    </Box>

                    {project.tags && project.tags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {project.tags.map((tag, idx) => (
                                <Chip
                                    key={idx} label={tag} size="small"
                                    sx={{
                                        height: 20, fontSize: '0.65rem',
                                        bgcolor: stringToPastelColor(tag), color: stringToDarkColor(tag),
                                        fontWeight: 600, border: `1px solid ${stringToDarkColor(tag)}`,
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    <Box mt={2}>
                        <Tooltip title={`${progress}% Complete`} placement="top">
                            <Box>
                                <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 2, height: 8 }} />
                                <Box mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption">{progress}%</Typography>
                                    <Chip size="small" label={getProjectStatus(project)} color={statusColor[getProjectStatus(project)]} sx={{ fontWeight: 700, textTransform: 'capitalize' }} />
                                </Box>
                            </Box>
                        </Tooltip>
                    </Box>

                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {project.updatedAt ? `Updated: ${new Date(project.updatedAt).toLocaleDateString()}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {project.deadline ? `Due: ${new Date(project.deadline).toLocaleDateString()}` : ''}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const ListRowMenu = ({ project, isSharedView }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const progress = getProjectProgress(project);
        const isFullyTranslated = progress >= 100;

        const handleOpen = (e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
        };
        const handleClose = () => {
            setAnchorEl(null);
        };

        return (
            <>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/projects/${getFirstProjectId(project)}/translate`); }} color="primary">
                    <TranslateIcon />
                </IconButton>
                <IconButton size="small" onClick={handleOpen} color="primary">
                    <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
                    {!isSharedView && (
                        <MenuItem onClick={() => { navigate(`/dashboard/projects/${project._id}/edit`); handleClose(); }}>
                            <i className="mdi mdi-pencil me-1"></i>Edit
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => { handleDownload(getFirstProjectId(project), 'original'); handleClose(); }}>
                        <i className="mdi mdi-download me-1"></i>Download Original
                    </MenuItem>
                    <MenuItem onClick={() => { handleDownload(getFirstProjectId(project), 'translated'); handleClose(); }} disabled={!isFullyTranslated}>
                        <i className="mdi mdi-download me-1"></i>Download Translated
                    </MenuItem>
                    <MenuItem onClick={() => { handleExportAsXliff(getFirstProjectId(project)); handleClose(); }}>
                        <i className="mdi mdi-file-export me-1"></i>Export as XLIFF
                    </MenuItem>
                    {!isSharedView && (
                        <MenuItem onClick={() => { handleDeleteProject(project._id); handleClose(); }}>
                            <i className="mdi mdi-delete me-1"></i>Delete Project
                        </MenuItem>
                    )}
                </Menu>
            </>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 5 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link color="inherit" onClick={() => navigate('/dashboard/projects')} sx={{ cursor: 'pointer' }}>
                    <FolderIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Projects
                </Link>
                {breadcrumbs}
            </Breadcrumbs>

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h4" fontWeight={800}>
                    Translation Projects
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                        size="small" variant="outlined" placeholder="Search by name, status, language..."
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
                        sx={{ minWidth: 260, mr: 2 }}
                    />
                    <ToggleButtonGroup
                        value={viewMode} exclusive onChange={handleViewToggle} size="small"
                        sx={{ background: '#f2f5fa', borderRadius: 2, boxShadow: 1 }}
                    >
                        <ToggleButton value="card"><ViewModuleIcon /></ToggleButton>
                        <ToggleButton value="list"><ViewListIcon /></ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="contained" onClick={() => navigate('/dashboard/new-translation')}>
                        Create New Project
                    </Button>
                </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="project tabs">
                    <Tab label={`My Projects (${createdProjects.length})`} />
                    <Tab label={`Shared With Me (${sharedProjects.length})`} />
                </Tabs>
            </Box>

            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Autocomplete
                        multiple size="small" options={allUniqueTags} value={tagFilter}
                        onChange={(e, newValue) => setTagFilter(newValue)}
                        renderInput={(params) => <TextField {...params} placeholder="Filter by tags" variant="outlined" sx={{ minWidth: 200 }} />}
                        sx={{ flexGrow: 1 }}
                    />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {['in progress', 'translated', 'complete'].map((statusKey) => (
                            <Chip
                                key={statusKey} label={statusKey} size="small" clickable
                                color={statusColor[statusKey]}
                                onClick={() => toggleStatus(statusKey)}
                                variant={statusFilter.includes(statusKey) ? 'filled' : 'outlined'}
                                sx={{ textTransform: 'capitalize', height: 28 }}
                            />
                        ))}
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={sortBy} label="Sort By"
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <MenuItem value="createdAt">Date Created</MenuItem>
                            <MenuItem value="deadline">Deadline</MenuItem>
                            <MenuItem value="progress">Progress</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {selectedProjects.length > 0 && (
                <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.light' }}>
                    <Typography variant="subtitle1" fontWeight={600} color="white">
                        {selectedProjects.length} Projects Selected
                    </Typography>
                    <Button variant="contained" color="secondary" onClick={() => handleBulkDownload('translated')}>
                        Bulk Download Translated
                    </Button>
                    <Button variant="contained" color="secondary" onClick={() => handleBulkDownload('original')}>
                        Bulk Download Original
                    </Button>
                    {tabValue === 0 && (
                        <Button variant="outlined" color="error" onClick={() => {
                            selectedProjects.forEach(id => handleDeleteProject(id));
                        }} sx={{ bgcolor: 'white' }}>
                            Bulk Delete
                        </Button>
                    )}
                </Paper>
            )}

            {filteredAndSortedProjects.length === 0 ? (
                 <Box textAlign="center" sx={{ mt: 4 }}>
                    <Typography variant="h6">No projects found.</Typography>
                 </Box>
            ) : viewMode === 'card' ? (
                <>
                    <Grid container spacing={3}>
                        {displayedProjects.map((project) => (
                            <Grid item xs={12} sm={6} md={4} key={project._id}>
                                <ProjectCard project={project} isSharedView={tabValue === 1} />
                            </Grid>
                        ))}
                    </Grid>
                    {filteredAndSortedProjects.length > visibleCount && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button variant="outlined" onClick={() => setVisibleCount(prev => prev + LOAD_MORE_STEP)}>
                                Show More
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox 
                                            indeterminate={selectedProjects.length > 0 && selectedProjects.length < displayedProjects.length}
                                            checked={displayedProjects.length > 0 && selectedProjects.length === displayedProjects.length}
                                            onChange={(e) => handleSelectAll(e, displayedProjects)}
                                        />
                                    </TableCell>
                                    <TableCell></TableCell> {/* Pin col */}
                                    <TableCell>Project Name</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Target</TableCell>
                                    <TableCell>Progress</TableCell>
                                    <TableCell>Deadline</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedProjects.map((project) => {
                                    const progress = getProjectProgress(project);
                                    const isPinned = pinnedProjects.includes(project._id);
                                    const isSelected = selectedProjects.includes(project._id);
                                    return (
                                        <TableRow
                                            key={project._id} hover sx={{ cursor: 'pointer' }}
                                            selected={isSelected}
                                            onClick={() => navigate(`/dashboard/projects/${project._id}`)}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isSelected} onClick={(e) => toggleSelection(e, project._id)} />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={(e) => togglePin(e, project._id)}>
                                                    {isPinned ? <PushPinIcon color="primary" fontSize="small" sx={{ transform: 'rotate(45deg)' }}/> : <PushPinOutlinedIcon fontSize="small" color="disabled"/>}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography fontWeight={isPinned ? 700 : 400}>{project.projectName}</Typography>
                                                    {project.tags && project.tags.length > 0 && (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {project.tags.map((tag, idx) => (
                                                                <Chip key={idx} label={tag} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: stringToPastelColor(tag), color: stringToDarkColor(tag) }} />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Flag code={getLanguageDetails(project.sourceLanguage).countryCode} style={{ width: 20, marginRight: 8 }} />
                                                {getLanguageDetails(project.sourceLanguage).name}
                                            </TableCell>
                                            <TableCell>
                                                <Flag code={getLanguageDetails(getProjectLangCodes(project)[0]).countryCode} style={{ width: 20, marginRight: 8 }} />
                                                {getProjectLangNames(project).join(', ')}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={`${progress}%`} placement="top">
                                                    <Box>
                                                        <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 2, height: 8, minWidth: 80 }} />
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{project.deadline ? new Date(project.deadline).toLocaleDateString() : 'N/A'}</TableCell>
                                            <TableCell><Chip size="small" label={getProjectStatus(project)} color={statusColor[getProjectStatus(project)]} /></TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDrawerProject(project); }}>
                                                        <InfoOutlinedIcon />
                                                    </IconButton>
                                                    <ListRowMenu project={project} isSharedView={tabValue === 1} />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {filteredAndSortedProjects.length > visibleCount && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button variant="outlined" onClick={() => setVisibleCount(prev => prev + LOAD_MORE_STEP)}>
                                Show More
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {/* Quick Peek Drawer */}
            <Drawer anchor="right" open={drawerProject !== null} onClose={() => setDrawerProject(null)}>
                <Box sx={{ width: 350, p: 3 }}>
                    {drawerProject && (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h5" fontWeight={700}>Project Details</Typography>
                                <IconButton onClick={() => setDrawerProject(null)}>&times;</IconButton>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="h6" mb={1}>{drawerProject.projectName}</Typography>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                {drawerProject.description || "No description provided."}
                            </Typography>
                            <Box mb={2}>
                                <Chip size="small" label={getProjectStatus(drawerProject)} color={statusColor[getProjectStatus(drawerProject)]} sx={{ textTransform: 'capitalize', mb: 1 }} />
                            </Box>
                            <Typography variant="body2" mb={1}><strong>Source:</strong> {getLanguageDetails(drawerProject.sourceLanguage).name}</Typography>
                            <Typography variant="body2" mb={1}><strong>Targets:</strong> {getProjectLangNames(drawerProject).join(', ')}</Typography>
                            <Typography variant="body2" mb={1}><strong>Created:</strong> {new Date(drawerProject.createdAt).toLocaleString()}</Typography>
                            <Typography variant="body2" mb={1}><strong>Deadline:</strong> {drawerProject.deadline ? new Date(drawerProject.deadline).toLocaleDateString() : 'N/A'}</Typography>
                            
                            <Box mt={3}>
                                <Button fullWidth variant="contained" onClick={() => navigate(`/dashboard/projects/${drawerProject._id}`)}>
                                    View Full Project Dashboard
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Drawer>
        </Container>
    );
}
